/* istanbul ignore file */
import React from 'react';
import capitalize from 'lodash/capitalize';
import { differenceInDays } from 'date-fns';
import { Flex, Text } from '@atlaskit/primitives';
import {
  EntitlementTags,
  CloudSiteLink,
  getSelectedColumns,
  NoData,
  OrgNameLink,
  SoldToAddress,
  SoldToCompanyName,
  BillingProfileNameFromId,
  Usage,
} from '@css-xp/ppc-common';
import upperCase from 'lodash/upperCase';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { Checkbox } from '@atlaskit/checkbox';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import Tooltip from '@atlaskit/tooltip';
import { CloudProductDetailView, isActiveSubscription, EntitlementStatus } from '@css-xp/base/entitlement-utils';

import { FormattedDate, HeadCell, SearchResultColumn, CopyableText } from '@css-xp/base';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { generatedTypesNew } from '@css-xp/graphtypes';
import {
  BillingStatus,
  CommerceAddress,
  ContactInfo,
  FetchInvoiceGroupDetailsForSubscriptionTablePpcQuery,
  FetchInvoiceGroupDetailsForSubscriptionTablePpcQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { SUBSCRIPTION_CONSTANTS, routePaths } from '@css-xp/ppc-constants';
import { usePersistedCacheQuery } from '@css-xp/ppc-custom-hooks';
import {
  getChargeElementV2,
  getIsBillingProfileUsedForInternalUsageLicense,
  getNextBillOrEndDate,
  getRouteUrl,
  isBilledElsewhereProduct,
  isFutureDatedInactiveSubscription,
  splitAndCapitalize,
  isOrgProduct,
} from '@css-xp/ppc-utils';
import { LastInvoiceAmountCell } from './components';
import { AppsCountCell } from './components/apps-count';
import { CustomerContactsAvatarGroup } from './components/customer-contacts-avatar-group';
import { fetchInvoiceGroupDetailsForSubscriptionTablePPCQuery } from './queries/fetch-invoice-group-details-for-subscription-table-ppc';
import { AddOnsCountCell } from './components/add-ons-count-cell';
import { IncludedProductsCell } from './components/included-products-cell';
import './subscription-table.scss';

type SubscriptionTableRowProps = {
  product: generatedTypesNew.TransactionAccountProduct;
  transactionAccountId: string | undefined;
  columnsList: Array<HeadCell & SearchResultColumn>;
  subscriptionColumnVersion?: number;
  isRowSelectionRequired?: boolean;
  onRowSelected?: (_id: string) => void;
  isRowSelected?: boolean;
  isSandboxSupportForSubscriptionsEnabled?: boolean;
  isNonCommercialPricingEnabled?: boolean;
  isFutureDatedSubscriptionsEnabled?: boolean;
  eccDetails: Array<ContactInfo>;
  relatedDomains: string[];
  isInternalUsageLicensesEnabled?: boolean;
  isSupportForManuallyBilledSubsEnabled?: boolean;
  isDesignIteration1Enabled?: boolean;
  isEntitlementOnGracePeriod?: boolean;
  isAppificationEnabled?: boolean;
};

const CellLoader = () => (
  <SkeletonLoaderWrapper>
    <SkeletonLoader width="60%" marginBottom="0" />
  </SkeletonLoaderWrapper>
);

export const getStatusForProductSubscription = (
  product: generatedTypesNew.TransactionAccountProduct,
  isFutureDatedInactiveEntitlement?: boolean
) => (
  <td className="status-cell">
    <EntitlementStatus
      billingStatus={product?.billingStatus}
      provisioningStatus={product?.provisioningStatus}
      scheduledChanges={product?.scheduledChanges}
      trial={product?.trial || false}
      upgradeTrial={!!product?.upgradeTrial}
      isFutureDatedInactiveEntitlement={isFutureDatedInactiveEntitlement}
      hideInactiveStatus={isFutureDatedInactiveEntitlement}
    />
  </td>
);

export const SubscriptionTableRow = ({
  product,
  transactionAccountId,
  columnsList,
  subscriptionColumnVersion,
  isRowSelectionRequired = false,
  isRowSelected,
  onRowSelected,
  isSandboxSupportForSubscriptionsEnabled = false,
  isNonCommercialPricingEnabled = false,
  isFutureDatedSubscriptionsEnabled = false,
  eccDetails,
  relatedDomains,
  isInternalUsageLicensesEnabled = false,
  isSupportForManuallyBilledSubsEnabled,
  isDesignIteration1Enabled,
  isEntitlementOnGracePeriod,
  isAppificationEnabled,
}: SubscriptionTableRowProps) => {
  const subscriptionsRoutePPC =
    transactionAccountId && getRouteUrl(routePaths.subscriptions, { txaId: transactionAccountId });

  const { data: invoiceGroupDetails, loading: invoiceGroupLoading } = usePersistedCacheQuery<
    FetchInvoiceGroupDetailsForSubscriptionTablePpcQuery,
    FetchInvoiceGroupDetailsForSubscriptionTablePpcQueryVariables
  >(fetchInvoiceGroupDetailsForSubscriptionTablePPCQuery, {
    variables: { id: product?.billingGroupId ?? '', txaId: transactionAccountId ?? '' },
    skip: !product?.billingGroupId || !transactionAccountId,
  });

  const isSubscriptionUsedForInternalUsageLicense =
    isInternalUsageLicensesEnabled && !invoiceGroupLoading
      ? getIsBillingProfileUsedForInternalUsageLicense({
          eccDetails,
          shipToPartyId: invoiceGroupDetails?.invoiceGroup?.shipToParty?.id,
          relatedDomains: relatedDomains,
        })
      : false;

  const getRowData = () => {
    const columns: (HeadCell & SearchResultColumn)[] = getSelectedColumns(columnsList);
    const renderChargeElement = (product: generatedTypesNew.TransactionAccountProduct, className?: string) => {
      return (
        product?.billedUsers && (
          <div className={className ?? ''}>
            {capitalize(
              getChargeElementV2({
                productId: product?.productId as string,
                ...(Number(product?.billedUsers) > 1 && { labelPlural: true }),
                edition: product?.edition as string,
                chargeElement: product?.chargeQuantities?.[0]?.chargeElement as string,
              })
            )}
          </div>
        )
      );
    };
    const renderBilledUsers = (product: generatedTypesNew.TransactionAccountProduct) => (
      <div>{product?.billedUsers ?? <NoData />}</div>
    );

    const rowData = columns.map((column) => {
      const isFutureDatedInactiveEntitlement =
        isFutureDatedSubscriptionsEnabled &&
        isFutureDatedInactiveSubscription(
          !!product?.isFutureDatedSubscription,
          product?.billingStatus as BillingStatus
        );
      switch (column.name) {
        case SUBSCRIPTION_CONSTANTS.COLUMNS.SUBSCRIPTION:
          return (
            <>
              <td className={isRowSelectionRequired ? 'row-selection-container row-selection-min-height' : ''}>
                {isRowSelectionRequired && (
                  <Checkbox
                    className="selection-checkbox"
                    testId={`select-subscription-checkbox-${product?.entitlementNumber?.toLowerCase()}`}
                    isChecked={isRowSelected}
                    onChange={() => {
                      onRowSelected && onRowSelected(product?.id);
                    }}
                    label=""
                  />
                )}
                <div className={isRowSelectionRequired ? 'product-name-header' : ''}>
                  <CloudProductDetailView
                    productId={product?.productId as string}
                    productName={product?.productName}
                    productLink={`${subscriptionsRoutePPC}${product?.id}`}
                    logoId={product?.logoId as string}
                    version={subscriptionColumnVersion}
                    {...(subscriptionColumnVersion === 2 && {
                      entitlementNumber: product?.entitlementNumber ?? '',
                      isEntitlementNumberCopyable: true,
                    })}
                    {...(!isDesignIteration1Enabled && {
                      billedElsewhere: isBilledElsewhereProduct(product?.productId),
                      isSandbox: (isSandboxSupportForSubscriptionsEnabled && product?.isSandbox) ?? false,
                      isAddOn: !!product?.isAddOn,
                      isExternallyBilled: Boolean(product?.isExternallyBilled),
                      isInternalUsageLicenseEntitlement: isSubscriptionUsedForInternalUsageLicense,
                    })}
                  />
                </div>
                {isDesignIteration1Enabled && (
                  <Flex alignItems="center">
                    <EntitlementTags
                      isAddOn={!!product?.isAddOn}
                      isSandbox={(isSandboxSupportForSubscriptionsEnabled && product?.isSandbox) ?? false}
                      isEntitlementOnGracePeriod={!!isEntitlementOnGracePeriod}
                      isExternallyBilled={!!product?.isExternallyBilled}
                      isInternalUsageLicenseEntitlement={isSubscriptionUsedForInternalUsageLicense}
                      isBilledElsewhere={isBilledElsewhereProduct(product?.productId)}
                    />
                  </Flex>
                )}
              </td>
            </>
          );
        case SUBSCRIPTION_CONSTANTS.COLUMNS.STATUS:
          return getStatusForProductSubscription(product, isFutureDatedInactiveEntitlement);
        case SUBSCRIPTION_CONSTANTS.COLUMNS.APPS:
          return <AppsCountCell subscription={product} baseRoute={subscriptionsRoutePPC} />;
        case SUBSCRIPTION_CONSTANTS.COLUMNS.ADD_ONS:
          return <AddOnsCountCell subscription={product} baseRoute={subscriptionsRoutePPC} />;
        case SUBSCRIPTION_CONSTANTS.COLUMNS.OFFERING:
          return (
            <td>
              {product?.offering?.name ?? product?.edition ?? <NoData />}
              {isNonCommercialPricingEnabled && (
                <Text size="small" color="color.text.subtlest" as="p">
                  {splitAndCapitalize(product?.pricingPlan?.type ?? '')}
                  {subscriptionColumnVersion === 2 && product?.billingFrequency
                    ? `-${capitalize(product?.billingFrequency)}`
                    : ''}
                </Text>
              )}
            </td>
          );
        case SUBSCRIPTION_CONSTANTS.COLUMNS.ORGANIZATION_NAME: {
          const { orgName, orgId } = product || {};
          return (
            <td>
              <OrgNameLink orgName={orgName} orgId={orgId} txaId={transactionAccountId} />
            </td>
          );
        }
        case SUBSCRIPTION_CONSTANTS.COLUMNS.SITE: {
          const siteName = product?.cloudSiteHostname;
          return (
            <td>
              <CloudSiteLink
                siteName={siteName}
                txaId={transactionAccountId}
                cloudSiteId={product?.cloudSiteId}
                shouldRemoveLineClamp={true}
                isOrgProductEntitlement={
                  isDesignIteration1Enabled &&
                  isOrgProduct({
                    productIdOrKey: product?.productId,
                    offeringName: product?.edition,
                    relatesFromEntitlements: product?.relatesFromEntitlements,
                    offering: product?.offering,
                  })
                }
              />
            </td>
          );
        }
        case SUBSCRIPTION_CONSTANTS.COLUMNS.COMPANY:
        case SUBSCRIPTION_CONSTANTS.COLUMNS.CUSTOMER:
          return (
            <td>
              {invoiceGroupLoading ? (
                <CellLoader />
              ) : (
                <SoldToCompanyName
                  txaId={transactionAccountId}
                  companyName={invoiceGroupDetails?.invoiceGroup?.shipToParty?.name}
                  shouldRemoveLineClamp={true}
                />
              )}
            </td>
          );
        case SUBSCRIPTION_CONSTANTS.COLUMNS.SOLD_TO_ADDRESS:
          return (
            <td>
              {invoiceGroupLoading ? (
                <CellLoader />
              ) : (
                <SoldToAddress
                  txaId={transactionAccountId}
                  shipTo={invoiceGroupDetails?.invoiceGroup?.shipToParty as CommerceAddress}
                />
              )}
            </td>
          );
        case SUBSCRIPTION_CONSTANTS.COLUMNS.CUSTOMER_CONTACTS:
          return (
            <td>
              <CustomerContactsAvatarGroup contacts={product.endCustomerContacts} />
            </td>
          );
        case SUBSCRIPTION_CONSTANTS.COLUMNS.BILLED_USERS:
          return (
            <td>
              <div>
                {renderChargeElement(product)}
                {renderBilledUsers(product)}
              </div>
            </td>
          );
        case SUBSCRIPTION_CONSTANTS.COLUMNS.USAGE:
          if (isDesignIteration1Enabled) {
            return (
              <td>
                <Usage
                  activeUsers={product?.activeUsers}
                  userTier={product?.userTier}
                  productId={product?.productId}
                  edition={product?.edition}
                  chargeElement={product?.chargeQuantities?.[0]?.chargeElement}
                />
              </td>
            );
          }
          return (
            <td>
              {renderBilledUsers(product)}
              {renderChargeElement(product, 'charge-element-text')}
            </td>
          );
        case SUBSCRIPTION_CONSTANTS.COLUMNS.RENEW_ON: {
          // Currently, the backend data (billingStatus and provisioningStatus) is not transformed.
          // Hence, using the upperCase method.
          const billingStatus = upperCase(product?.billingStatus ?? '');
          const provisioningStatus = upperCase(product?.provisioningStatus ?? '');
          const nextBillOrEndDate = getNextBillOrEndDate(
            product as generatedTypesNew.EntitlementDetails,
            isSupportForManuallyBilledSubsEnabled,
            isEntitlementOnGracePeriod
          );
          const renewInDays = nextBillOrEndDate && differenceInDays(new Date(nextBillOrEndDate), new Date());

          let tooltipContent = '';
          let shouldShowWarning = false;
          if (typeof renewInDays === 'number') {
            if (renewInDays === 0) {
              tooltipContent = 'today';
            } else if (renewInDays === 1) {
              tooltipContent = 'in 1 day';
            } else {
              tooltipContent = `in ${renewInDays} days`;
            }

            if (isDesignIteration1Enabled) {
              tooltipContent = `Renews ${tooltipContent}`;
            }

            if (renewInDays < 8) {
              shouldShowWarning = true;
            }
          }

          const warningIcon = (
            <span className="warning-icon-container" data-testid="warning-icon-container">
              <WarningIcon label="warning" primaryColor="orange" />{' '}
            </span>
          );

          return (
            <td>
              {nextBillOrEndDate && isActiveSubscription(billingStatus, provisioningStatus) ? (
                <div>
                  {shouldShowWarning &&
                    (isDesignIteration1Enabled ? (
                      <Tooltip tag="span" content={tooltipContent}>
                        {warningIcon}
                      </Tooltip>
                    ) : (
                      warningIcon
                    ))}
                  <FormattedDate date={nextBillOrEndDate} format={NEW_DATE_FORMAT} />
                  &nbsp;
                  {!isDesignIteration1Enabled && (
                    <Tooltip tag="span" content={tooltipContent}>
                      <InfoIcon label="info" size="small" />
                    </Tooltip>
                  )}
                </div>
              ) : (
                <NoData />
              )}
            </td>
          );
        }
        case SUBSCRIPTION_CONSTANTS.COLUMNS.BILLING_CYCLE:
          return <td>{product?.billingFrequency ? capitalize(product.billingFrequency) : <NoData />}</td>;
        case SUBSCRIPTION_CONSTANTS.COLUMNS.ENTITLEMENT_NUMBER:
          return (
            <td>{(product?.entitlementNumber && <CopyableText text={product?.entitlementNumber} />) ?? <NoData />}</td>
          );
        case SUBSCRIPTION_CONSTANTS.COLUMNS.CREATED:
          return <td>{<FormattedDate date={product?.createdDate} format={NEW_DATE_FORMAT} />}</td>;
        case SUBSCRIPTION_CONSTANTS.COLUMNS.LAST_INVOICE_AMOUNT:
          return (
            <LastInvoiceAmountCell
              entitlementId={product?.id}
              transactionAccountId={transactionAccountId || ''}
              isRightAligned={isDesignIteration1Enabled}
            />
          );
        case SUBSCRIPTION_CONSTANTS.COLUMNS.BILLING_PROFILE:
          return (
            <td>
              <BillingProfileNameFromId
                id={product?.billingGroupId}
                txaId={transactionAccountId || ''}
                showBillingProfileNumber={true}
              />
            </td>
          );
        case SUBSCRIPTION_CONSTANTS.COLUMNS.INCLUDED_PRODUCTS:
          return (
            <IncludedProductsCell
              transactionAccountId={transactionAccountId || ''}
              product={product}
              version={subscriptionColumnVersion}
              isAppificationEnabled={isAppificationEnabled}
            />
          );
      }
    });

    return rowData;
  };

  return <tr>{getRowData()}</tr>;
};
